import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export interface NotificationPreferences {
  messages_enabled: boolean;
  campaigns_enabled: boolean;
  templates_enabled: boolean;
  contact_import_enabled: boolean;
  team_invitations_enabled: boolean;
  business_account_enabled: boolean;
  system_enabled: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  quiet_hours_timezone: string;
}

export const pushNotificationsApi = {
  async getPreferences(): Promise<NotificationPreferences> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/notifications/preferences`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to fetch notification preferences');
    }
    return json.data as NotificationPreferences;
  },

  async updatePreferences(prefs: Partial<NotificationPreferences>): Promise<NotificationPreferences> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/notifications/preferences`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(prefs),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to update notification preferences');
    }
    return json.data as NotificationPreferences;
  },

  async registerDevice(token: string, deviceType: 'android' | 'ios' | 'web') {
    const res = await fetch(`${API_CONFIG.BASE_URL}/notifications/push/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ token, device_type: deviceType }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to register device for push notifications');
    }
    return json;
  },

  async sendTestNotification() {
    const res = await fetch(`${API_CONFIG.BASE_URL}/notifications/push/test`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to send test notification');
    }
    return json;
  },

  async unregisterDevice(token: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/notifications/push/unregister`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ token }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to unregister device');
    }
    return json;
  },
};

export const WEB_PUSH_TOKEN_KEY = 'fcm_web_token';
// The user the stored web token was registered for. A browser's FCM token is
// shared by everyone who signs in on it, so without this a second user inherits
// the first user's registration and receives THEIR pushes.
export const WEB_PUSH_TOKEN_OWNER_KEY = 'fcm_web_token_user';

/*
 * Who is signed in on this browser, as the push SERVICE WORKER sees it.
 *
 * A background push is rendered by firebase-messaging-sw.js while no tab is
 * open, and a service worker can read neither localStorage nor the page's
 * memory. Cache Storage is same-origin, readable from both, and survives the
 * worker being terminated — so the page records the identity here and the
 * worker drops a push whose `recipient_user_id` is someone else (a token still
 * bound to a previous user of this browser). Keep the cache name and key in
 * step with firebase-messaging-sw.js.
 */
export const PUSH_IDENTITY_CACHE = 'wenext-push-identity';
export const PUSH_IDENTITY_KEY = '/__wenext/push-identity';

/** `userId` = the signed-in user; `null` = signed out (show nobody's pushes). */
export async function setPushIdentity(userId: string | null): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open(PUSH_IDENTITY_CACHE);
    await cache.put(
      PUSH_IDENTITY_KEY,
      new Response(JSON.stringify({ userId, updatedAt: Date.now() }), {
        headers: { 'Content-Type': 'application/json' },
      })
    );
  } catch {
    // Storage blocked (private mode, quota): the worker then falls back to
    // its previous behaviour, and the token release still applies.
  }
}

// Detach this browser's push token from the signed-in user. Must run BEFORE the
// auth token is cleared (the unregister call is authenticated). Bounded so a
// slow network never holds up sign-out; local keys are cleared either way.
export async function releaseWebPushDevice(
  timeoutMs = 3000,
  { signedOut = true }: { signedOut?: boolean } = {}
): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(WEB_PUSH_TOKEN_KEY);
  } catch {
    return;
  }
  try {
    if (token) {
      await Promise.race([
        pushNotificationsApi.unregisterDevice(token),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
    }
  } catch {
  } finally {
    try {
      localStorage.removeItem(WEB_PUSH_TOKEN_KEY);
      localStorage.removeItem(WEB_PUSH_TOKEN_OWNER_KEY);
    } catch {
    }
    // Signed out: the worker shows no addressed push until someone signs in.
    // Not when a signed-in user is merely detaching a stale token.
    if (signedOut) await setPushIdentity(null);
  }
}
